import { useEffect, useRef } from 'react';

import { m } from './shared';

const GLYPHS = 'ｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜﾝ0123456789<>[]{}/\\|=+*';
const STEP = 21;
const FONT = '14px ui-monospace, Menlo, Consolas, monospace';

const glyph = () => GLYPHS.charAt(Math.floor(Math.random() * GLYPHS.length));

/**
 * The design's digital rain (canvas, ~16 fps). It runs only while its band is on screen and the tab is visible;
 * under prefers-reduced-motion it never starts and holds one settled frame, following a live change of that
 * setting. Decorative.
 */
export function DigitalRain({ variant }: { variant: 'hero' | 'end' }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    let ctx: CanvasRenderingContext2D | null = null;
    try {
      ctx = canvas?.getContext('2d') ?? null;
    } catch {
      ctx = null;
    }
    if (!canvas || !ctx) return undefined;
    const context = ctx;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const motionQuery = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    let width = 0;
    let height = 0;
    let cols = 0;
    let drops: number[] = [];
    let speeds: number[] = [];
    let raf = 0;
    let last = 0;
    let running = false;
    let visible = false;
    let allowed = true;

    const size = () => {
      const rect = canvas.getBoundingClientRect();
      const nextWidth = Math.max(1, Math.round(rect.width));
      const nextHeight = Math.max(1, Math.round(rect.height));
      if (nextWidth === width && nextHeight === height) return false;
      width = nextWidth;
      height = nextHeight;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(width / STEP);
      drops = Array.from({ length: cols }, () => (Math.random() * -height) / STEP);
      speeds = Array.from({ length: cols }, () => 0.55 + Math.random() * 0.8);
      context.clearRect(0, 0, width, height);
      return true;
    };

    const draw = (fade: number) => {
      context.fillStyle = `rgba(7, 10, 8, ${fade})`;
      context.fillRect(0, 0, width, height);
      context.font = FONT;
      context.textBaseline = 'top';
      for (let col = 0; col < cols; col += 1) {
        const x = col * STEP;
        const y = (drops[col] ?? 0) * STEP;
        if (y > -STEP && y < height) {
          context.fillStyle = 'rgba(198, 255, 212, .85)';
          context.fillText(glyph(), x, y);
          context.fillStyle = 'rgba(61, 255, 122, .34)';
          context.fillText(glyph(), x, y - STEP);
          context.fillStyle = 'rgba(61, 255, 122, .14)';
          context.fillText(glyph(), x, y - STEP * 2);
        }
        drops[col] = (drops[col] ?? 0) + (speeds[col] ?? 1);
        if (y > height + STEP * 3 && Math.random() > 0.975) drops[col] = Math.random() * -12;
      }
    };

    // One settled frame for the paused / reduced-motion state: never a blank band.
    const still = () => {
      context.clearRect(0, 0, width, height);
      context.font = FONT;
      context.textBaseline = 'top';
      for (let col = 0; col < cols; col += 1) {
        const x = col * STEP;
        const count = 5 + Math.floor(Math.random() * 14);
        const top = Math.random() * height;
        for (let row = 0; row < count; row += 1) {
          const y = top + row * STEP;
          if (y > height) break;
          const alpha = row === count - 1 ? 0.72 : Math.max(0.05, 0.32 - row * 0.02);
          context.fillStyle = row === count - 1 ? `rgba(190, 255, 205, ${alpha})` : `rgba(61, 255, 122, ${alpha})`;
          context.fillText(glyph(), x, y);
        }
      }
    };

    const loop = (time: number) => {
      if (!running) return;
      raf = window.requestAnimationFrame(loop);
      if (time - last < 62) return;
      last = time;
      draw(0.09);
    };
    const start = () => {
      if (running || !allowed || !visible) return;
      running = true;
      last = 0;
      raf = window.requestAnimationFrame(loop);
    };
    const stop = () => {
      running = false;
      if (raf) window.cancelAnimationFrame(raf);
      raf = 0;
    };
    const sync = () => {
      allowed = !document.hidden && !motionQuery?.matches;
      if (!allowed) {
        stop();
        still();
      } else start();
    };

    size();
    still();

    let observer: IntersectionObserver | null = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            visible = entry.isIntersecting;
            if (visible) start();
            else stop();
          });
        },
        { rootMargin: '120px 0px', threshold: 0 }
      );
      observer.observe(canvas);
    } else {
      visible = true;
    }

    let resizeTimer = 0;
    const onResize = () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        if (size() && !running) still();
      }, 180);
    };
    window.addEventListener('resize', onResize);
    document.addEventListener('visibilitychange', sync);
    motionQuery?.addEventListener?.('change', sync);
    sync();

    return () => {
      stop();
      observer?.disconnect();
      window.clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', sync);
      motionQuery?.removeEventListener?.('change', sync);
    };
  }, []);

  return (
    <div className={m(variant === 'end' ? 'mx-rain mx-rain--end' : 'mx-rain')} aria-hidden='true'>
      <canvas ref={canvasRef} className='block size-full opacity-50' />
    </div>
  );
}
